import { t } from '@/utils/i18n'
import dayjs from 'dayjs'
import { Toast } from 'antd-mobile'
import { matchPath } from 'react-router-dom'
import utils from './utils'
import localStorage from './localStorage'
import apis from '@/apis'
import cal from './cal'
import image from './image'
import { Order } from '@/pages/Order/types'
import { corpInfoActionCreator } from '@/stores/corp-info'
import { corpSettingActionCreator } from '@/stores/corp-setting'
import { setCart as setCartActionCreator } from '@/stores/cart'
import { store } from '@/stores/store'
import { getConfig } from '@/config/config'
import { APP_ENV_MAP, CLUSTER_LIST } from '@/constant/base-const'
import router from '@/router'
import routes, { routesMap } from '@/router/routes'
import type { ProductItem, ProductDetail } from '@/types/product'

const { getUuid, to, debounce } = utils
const { getLocalStorage, setLocalStorage, removeLocalStorage } = localStorage
const { moneyFormat } = cal
const { corpApi, basicApi, authApi } = apis
const { dispatch } = store
const setCorpInfo = (payload: any) => dispatch(corpInfoActionCreator(payload))
const setCorpSetting = (payload: any) =>
  dispatch(corpSettingActionCreator(payload))
const setCart = (payload: any) => dispatch(setCartActionCreator(payload))

/**
 * 把列表转为树
 * @param list
 * @param key
 * @param parentKey
 * @returns
 */
function transListToTree(
  list: any[] = [],
  key = 'id',
  parentKey = 'parentId',
): any[] {
  if (!list?.length) return []
  const tree: any[] = []
  const map: {
    [key: string]: any
  } = {}
  list.forEach((item: any) => {
    map[item[key]] = item
  })

  list.forEach((item: any) => {
    const parent = map[item[parentKey]]

    if (parent) {
      parent.children = parent.children || []
      parent.children.push(item)
    } else {
      tree.push(item)
    }
  })

  return tree
}

/**
 * 获取文件路径名
 * @returns
 */
function getFilePathName() {
  const prefix = 'pai_fa/avatar/'
  const date = dayjs().format('YYYY-MM-DD')
  const uuid = getUuid()
  return prefix + date + '/' + uuid
}

// 根据默认单位索引取单位对象
function getChooseUnit(
  product: ProductItem,
  corpSetting: any,
  detail: ProductDetail,
) {
  const { cloudProduct } = product || {}
  let { productStock } = cloudProduct || {}
  const { price, unitIndex, priceUnitIndex } = detail || {}
  const { productLockStock, productMoqQty } = cloudProduct || {}
  const unitPriceArray = cloudProduct?.unitPriceArray || []
  const chooseUnit = unitPriceArray[unitIndex] || {} // 选择的数量单位
  const priceUnit = unitPriceArray[priceUnitIndex] || {} // 选择的价格单位

  // 如果选择了可售库存，则可售库存=库存-锁定库存
  const cloudStoreStockType = corpSetting.cloudStoreStockType || 0
  if (cloudStoreStockType === 1 && productLockStock) {
    productStock = cal.minus(productStock, productLockStock)
  }

  // 记录当前单位索引
  chooseUnit.unitIndex = unitIndex

  /**
   * 单位下总库存（向下取整）
   * prod.productStock 基础单位库存
   * chooseUnit.unitRatio 单位比例
   * 解释：基础单位的意思是商品的最小单位。比如卖矿泉水，最小是一瓶，基础单位是瓶。那么“瓶”的单位比例是1，一箱12瓶，“箱”的单位比例是12。假设基础单位库存24瓶，即prod.productStock为24，那么当客户选择瓶的时候，24/1，显示的库存是24瓶，当客户选择箱的试试，24/12，显示的库存是2箱
   */
  chooseUnit.stock = cal.getDown(
    corpSetting.qualityPrecision,
    cal.divide(productStock, Number(chooseUnit.unitRatio)),
  ) as number

  // 单位下起订量（向上取整）
  chooseUnit.productMoqQty = cal.getCarry(
    corpSetting.qualityPrecision,
    Number(productMoqQty),
  )

  chooseUnit.price = price

  chooseUnit.priceText = moneyFormat(price, corpSetting.pricePrecision)
  chooseUnit.originPrice = priceUnit.linePrice
  chooseUnit.originPriceText = moneyFormat(
    priceUnit.linePrice,
    corpSetting.pricePrecision,
  )
  chooseUnit.discount =
    priceUnit.linePrice && priceUnit.linePrice > price
      ? cal.getDown(
          2,
          cal.divide(priceUnit.linePrice - price, priceUnit.linePrice),
        )
      : 0
  chooseUnit.discountText = cal.times(chooseUnit.discount, 100) + '%'

  return chooseUnit
}

/**
 * 获取订单的状态
 * @param order
 * @returns
 */
function getOrderStatusLabel(order: Order) {
  const {
    orderTag,
    orderStatus,
    orderExtendEntity,
    source,
    deliveryOrders,
    deliveryTasks,
    deliveryStatus,
  } = order || {}
  const { logisticsStatus } = orderExtendEntity || {}
  const logisticsLabels = [
    t('采购中'),
    t('头程已发货'),
    t('已到中转仓'),
    t('跨境运输中'),
    t('待配送'),
    t('配送中'),
    t('已完成'),
    t('待自提'),
    t('仓库分拣中'),
  ]

  if ((orderStatus & 8) === 8) {
    return t('已作废')
  }

  // 退换单
  if ((orderTag & 1) > 0) {
    if ((orderExtendEntity.orderFlag & 32) > 0) {
      return t('退货成功')
    }

    if ((orderExtendEntity.orderFlag & 64) > 0) {
      return t('退货关闭')
    }

    if (
      (orderExtendEntity.orderFlag & 32) === 0 &&
      (orderExtendEntity.orderFlag & 64) === 0
    ) {
      return t('退货中')
    }

    return t('退换单')
  }

  if (
    (orderTag & 1) !== 0 &&
    (orderExtendEntity.orderFlag & 32) !== 0 &&
    ((deliveryOrders && deliveryOrders.length) ||
      (deliveryTasks && deliveryTasks.length)) &&
    deliveryStatus === 2
  ) {
    return t('已完成')
  }

  if (source === 2 && (orderStatus & 2) !== 2) {
    return t('待确认')
  }

  return logisticsLabels[logisticsStatus]
}

// 获取退货状态
function getOrderProductReturnStatusLabel(status: number) {
  if (status === 1) {
    return t('退货中')
  }

  if (status === 2) {
    return t('退货成功')
  }

  return ''
}

/**
 * 处理企业设置的数据
 * @param setting
 * @returns
 */
function handleCorpSetting(setting: any = {}) {
  const { cloudAttrs, cloudStoreFlag, currency } = setting || {}
  return {
    cloudAttrs, // 属性显示
    cloudStoreFlag,
    currencySymbol: currency,
    priceOff: !!(cloudStoreFlag & (2 << 0)), // 不显示价格
    priceOnCustomer: !!(cloudStoreFlag & (2 << 1)), // 客户可见价格
    priceOn: !!(cloudStoreFlag & (2 << 2)), // 全部人可见价格
    salesOn: !!(cloudStoreFlag & (2 << 5)), // 显示销售数量
    salesSlipOn: !!(cloudStoreFlag & (2 << 6)), // 显示销售次数
    stockShowOn: !!(cloudStoreFlag & (2 << 9)), // 显示库存
    stockOn: !!(setting.moduleFlag & (2 ** 0) && cloudStoreFlag & (2 << 11)), // 有货才可以下单
    cloudStoreStockType: setting.cloudStoreStockType || 0, // 库存类型：0实物库存，1可售库存
    startQuantityOn: !!(setting.productFlag & 1), // 起订量开关
    minNumStep: setting.minNumStep === 0, // 货品增加起订量递增规则
    qualityPrecision: setting.qualityPrecision || 0, // 数量精度
    pricePrecision: setting.pricePrecision || 0, // 价格精度
    cloudProductSort: setting.cloudProductSort, // 默认显示排序
  }
}

/**
 * 处理企业信息数据
 * @param setting
 * @returns
 */
function handleCorpInfo(setting: any = {}) {
  const {
    corpLogo,
    corpName,
    corpIntroWord,
    address,
    cloudStoreContactPhone,
    cloudStoreNotice,
    cloudStoreContactPerson,
  } = setting
  return {
    corpLogo,
    corpName,
    corpIntroWord,
    address,
    cloudStoreContactPhone,
    cloudStoreNotice,
    cloudStoreContactPerson,
  }
}

/**
 * 主动调接口去续票
 * @returns
 */
function toCheckExchangeToken() {
  const token = getLocalStorage('token')
  if (!token) return
  basicApi.getCurrencyList()
}

// 获取企业设置
async function getCorpSetting() {
  const corpSetting = getLocalStorage('corpSetting', true) || {}
  const corpInfo = getLocalStorage('corpInfo', true) || {}
  const { corpId, customerId } = corpInfo
  const [err, res] = await to(
    corpApi.getPaifaCorpSetting({ corpId, customerId }),
  )
  if (err || !res) return

  const setting = res.data || {}
  const newCorpSetting = handleCorpSetting(setting)
  const newCorpInfo = handleCorpInfo(setting)
  setCorpInfo({ ...corpInfo, ...newCorpInfo })
  setCorpSetting({ ...corpSetting, ...newCorpSetting })
}

// cod信息
async function getCodInfo() {
  const corpSetting = getLocalStorage('corpSetting', true) || {}
  const corpInfo = getLocalStorage('corpInfo', true) || {}
  const { corpId } = corpInfo
  // 获取公共配置
  const [err, res] = await to(basicApi.getLogistics({ corpId }))
  if (err || !res) return

  const cod = res.data || {}
  setCorpSetting({ ...corpSetting, cod })
}

/**
 * 轮询
 */
function timeLooperRequest(time = 15 * 60 * 1000) {
  window.clearInterval(window.loopTimer)
  const corpInfo = getLocalStorage('corpInfo', true) || {}
  const { corpId, customerId } = corpInfo
  if (!corpId || !customerId) return

  window.loopTimer = setInterval(() => {
    getCorpSetting()
    getCodInfo()
  }, time)
}

// 游客登录
async function onTempLogin(corpId: string) {
  const [err, res] = await to(
    authApi.paifaMallTempLogin({
      corpId,
    }),
  )

  if (err || !res) {
    return
  }

  const resData = res.data || {}
  storeCorpInfoTempLogin(resData)
  await updataConfig(resData)
}

// （临时登录）存储企业信息
function storeCorpInfoTempLogin(data: any) {
  const { apiUrl, pfUser, cart, token, paiFaFlag, setting } = data || {}
  const { customerId, accountId, email, phone, id: pfUserId } = pfUser || {}
  const corpId = data?.corpId || pfUser?.corpId
  const {
    address,
    cloudAttrs,
    cloudProductSort,
    cloudStoreContactPerson,
    cloudStoreContactPhone,
    cloudStoreFlag,
    corpIntroWord,
    corpLogo,
    corpName,
    pricePrecision,
    qualityPrecision,
    cloudStoreNotice,
    staffAvatar,
    staffEmail,
    staffName,
    staffPhone,
  } = setting || {}
  const { cartId } = cart || {}

  if (apiUrl) {
    setLocalStorage('apiUrl', apiUrl)
    // 设置集群
    const regex = /https?:\/\/[^/]+(\/[^/?#]+)/
    const match = apiUrl.match(regex)
    if (match && match[1]) {
      const pathname = match[1]
      setLocalStorage('cluster', pathname)
    }
  }

  const corpInfoData: any = {
    corpId,
    customerId,
    accountId,
    pfUserId,
    cartId,
    email,
    phone,
    address,
    cloudAttrs,
    cloudProductSort,
    cloudStoreContactPerson,
    cloudStoreContactPhone,
    cloudStoreFlag,
    corpIntroWord,
    corpLogo,
    corpName,
    pricePrecision,
    qualityPrecision,
    cloudStoreNotice,
    staffAvatar,
    staffEmail,
    staffName,
    staffPhone,
    pfUser,
    tempToken: token,
    paiFaFlag,
  }

  setCorpInfo(corpInfoData)
  setCart(cart)
}

// 存储企业信息
function storeCorpInfo(data: any) {
  const {
    apiUrl,
    ossUrl,
    pfUser,
    cart,
    token,
    setting,
    industryId,
    countryId,
  } = data || {}

  const { customerId, accountId, email, phone, id: pfUserId } = pfUser || {}

  const corpId = data?.corpId || pfUser?.corpId

  const {
    address,
    cloudAttrs,
    cloudProductSort,
    cloudStoreContactPerson,
    cloudStoreContactPhone,
    cloudStoreFlag,
    corpIntroWord,
    corpLogo,
    corpName,
    pricePrecision,
    qualityPrecision,
    cloudStoreNotice,
    staffAvatar,
    staffEmail,
    staffName,
    staffPhone,
  } = setting || {}

  const { cartId } = cart || {}

  if (apiUrl) {
    setLocalStorage('apiUrl', apiUrl)
    // 设置集群
    const regex = /https?:\/\/[^/]+(\/[^/?#]+)/
    const match = apiUrl.match(regex)

    if (match && match[1]) {
      const pathname = match[1]
      setLocalStorage('cluster', pathname)
    }
  }

  if (ossUrl) {
    setLocalStorage('ossRoot', ossUrl)
  }

  setCorpInfo({
    corpId,
    countryId,
    customerId,
    accountId,
    pfUserId,
    cartId,
    email,
    phone,
    token,
    address,
    cloudAttrs,
    cloudProductSort,
    cloudStoreContactPerson,
    cloudStoreContactPhone,
    cloudStoreFlag,
    corpIntroWord,
    corpLogo,
    corpName,
    pricePrecision,
    qualityPrecision,
    cloudStoreNotice,
    staffAvatar,
    staffEmail,
    staffName,
    staffPhone,
    pfUser,
    industryId,
  })

  setCart(cart)
  setLocalStorage('site', corpId)
}

/*
 * 更新配置信息
 * @params {object} options
 */
async function updataConfig(params: any) {
  const { setting, corpId } = params || {}
  const corpSetting = {}

  // 获取公共配置
  const [err, res] = await to(basicApi.getLogistics({ corpId }))
  const cod = (!err && res?.data) || {}

  if (!setting) {
    return
  }

  /**
   * cloudStoreFlag
   *
   * 1:向游客开放
   * 2:不显示价格
   * 2<<1:仅老客户显示价格
   * 2<<2:老客户和游客都显示价格
   * 2<<3:老客户优先报上次价
   * 2<<4:不显示销量
   * 2<<5:显示销售数量
   * 2<<6:显示销售次数
   * 2<<7:自动上架
   * 2<<8:客户下单强制支付
   * 2<<9:云店显示库存
   * 2<<10:小程序快速开单配置
   * 2<<11: 有货才可以下单
   * 2<<12: 游客下单强制支付
   * 2<<13:在线支付单据默认打印
   * 2<<14:是否允许下载图片
   * 2<<15:小程序电子单显示云店入口
   * 2<<16:库存0自动上架
   * 2<<18:购买权限
   *
   */
  Object.assign(corpSetting, {
    ...handleCorpSetting(setting),
    cod,
  })

  setCorpSetting(corpSetting)
}

/**
 * 根据app的env获取前端的环境
 * @returns
 */
function getAppEnv() {
  const resEnv = {
    env: '',
    reqUrl: '',
    ossUrl: '',
  }
  if (
    window?.webview?.getEnv &&
    typeof window?.webview?.getEnv === 'function'
  ) {
    let envInfo = window.webview.getEnv()
    if (envInfo && typeof envInfo === 'string') {
      envInfo = JSON.parse(envInfo)
    }
    console.log('envInfo', envInfo)
    const { env, reqUrl, ossUrl } = envInfo || {}
    resEnv.env = APP_ENV_MAP[env]
    resEnv.reqUrl = reqUrl
    resEnv.ossUrl = ossUrl
    console.log('从app获取的环境', envInfo)
    const lastEnv = getLocalStorage('resEnv')
    if (resEnv) setLocalStorage('resEnv', resEnv.env)
    // 如果这次获取的环境和上次的不一样，导致token不通用，就跳回登录页
    if (lastEnv !== resEnv.env && !isTempLoginPage()) {
      // 跳回登录页
      logout()
      // router.navigate(routesMap.Login)
    }
  }

  return resEnv
}

/**
 * 根据app的env获取前端的apiUrl
 * @returns
 */
function getApiByAppEnv() {
  const { env, reqUrl = '', ossUrl = '' } = getAppEnv()
  const config = getConfig(env) || {}
  let rootPath = config.rootPath
  const matchList = reqUrl && reqUrl.match(/https?:\/\/[^/]+(\/[^/?#]+)/)

  // app 下发域名存在集群取 app 的接口地址
  if (matchList && CLUSTER_LIST.includes(matchList[1])) {
    rootPath = reqUrl
  } else {
    rootPath = rootPath + (getLocalStorage('cluster') || '')
  }

  setLocalStorage('apiUrl', rootPath)
  // 如果储存里已经有登录下发的ossRoot，则不设置。如果为空，则设置配置的ossRoot。
  setLocalStorage('ossRoot', ossUrl || config.ossRoot)
  return config
}

// 退出登录
function logout(opt?: any) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Object.entries(opt || {}).forEach(([_key, value]) => {
    if (typeof value === 'function') {
      value()
    }
  })

  // 清除本地存储: 集群缓存
  removeLocalStorage('cluster')
  const loginPath = opt?.query ? routesMap.Login + opt.query : routesMap.Login
  router.navigate(loginPath)
}

/*
 * 判断是否有首页装修模块
 */
const hasHomeModule = async () => {
  const corpInfo = getLocalStorage('corpInfo', true) || {}
  const { corpId, industryId } = corpInfo

  const params = { corpId, publishStatus: 1, industryId }
  const [err, res] = await to(corpApi.queryHomeModule(params))
  if (err) {
    return false
  }

  // 筛选出已发布的模块
  const modules = (res?.data || [])
    .filter((module: any) => module.publishStatus === 1)
    .filter((module: any) => module.type !== 3) // 过滤掉产品模块

  if (!modules.length) {
    return false
  }

  return true
}

/*
 * 判断是否已经切换了企业，如果切换了企业就退出登录
 */
const checkCorpChange = async () => {
  // 如果是相册或营销临时页面，不执行切换企业退出登录逻辑
  if (isTempLoginPage()) {
    return
  }

  const site = getLocalStorage('site') || ''
  if (!site) {
    return
  }

  // 仅针对Invoice Maker Pro
  if (!window.webview?.getAuth) {
    return
  }

  try {
    const auth = JSON.parse(window.webview.getAuth())
    const corpId = auth.corpId || ''
    if (!corpId) {
      return
    }

    if (corpId !== site) {
      logout()
    }
  } catch (e) {
    console.log(e)
  }
}

/**
 * 批量下载文件，支持多个文件
 * 如果在webview环境，调用webview的下载方法
 * 如果在非webview环境，调用浏览器的下载方法
 * @param urls
 */
const downloadFiles = debounce(
  function onDownloadFiles(
    urlDatas: {
      url: string
      type: 'image' | 'video'
      name?: string
    }[],
  ) {
    if (!urlDatas || !urlDatas.length) return
    // 传给app环境的数据格式
    const appUrls = JSON.stringify(
      urlDatas.map((item) => ({
        path: item.url,
        isUrl: true, // 是否是远程链接
        isVideo: item.type === 'video',
      })),
    )

    if (window.webview?.downloadMedia) {
      window.webview.downloadMedia(appUrls)
    } else {
      Toast.show({
        icon: 'loading',
        content: t('正在下载'),
        duration: 1500,
      })

      const promiseAll = urlDatas.map((urlData) => {
        const { url, name } = urlData || {}
        return image.downloadFile(url, name)
      })

      Promise.all(promiseAll).finally(() => {
        Toast.show({
          icon: 'success',
          content: t('下载完成，在相册查看'),
          duration: 1500,
        })
      })
    }
  },
  350,
  true,
)

/**
 * 是否是临时登录页面
 * @returns
 */
function isTempLoginPage() {
  const href = window.location.href
  // 如果是相册或营销，就是临时登录页面
  if (href.includes('album') || href.includes('marketing')) {
    return true
  }
}

/**
 * 改变页面的标题
 * @param routes
 * @param pathname
 * @returns
 */
function changePageTitle(routes: any[], pathname: string) {
  const currentRoute = routes.find((route) => matchPath(route.path, pathname))

  if (!currentRoute) return

  if (currentRoute.state?.getTitle) {
    document.title = currentRoute.state.getTitle()
  } else if (currentRoute.state?.title) {
    document.title = currentRoute.state.title
  }
}

/**
 * 改变相册页面的标题
 */
function changeAlbumPageTitle() {
  // 获取当前页面的路由名
  const pathname = window.location.pathname
  changePageTitle(routes, pathname)
}

export default {
  transListToTree,
  getFilePathName,
  getChooseUnit,
  getOrderStatusLabel,
  getOrderProductReturnStatusLabel,
  handleCorpSetting,
  toCheckExchangeToken,
  timeLooperRequest,
  getCorpSetting,
  getCodInfo,
  onTempLogin,
  storeCorpInfo,
  storeCorpInfoTempLogin,
  getAppEnv,
  getApiByAppEnv,
  logout,
  hasHomeModule,
  checkCorpChange,
  downloadFiles,
  changePageTitle,
  changeAlbumPageTitle,
}
